import React, { useEffect, useState } from 'react';
import { navigate, useLocation } from '@reach/router';
import styled from 'styled-components';
import Text from 'components/Text';
import Logo from 'components/Logo';
import SetNewPasswordForm from 'components/SetNewPasswordForm';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { resetPassword, verifyTokenMatches } from 'api/auth';

const ResetPasswordPage = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  background: #e8eaeb;
`;

const ResetPasswordPanel = styled.div`
  width: 400px;
  background-color: #ffffff;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-top: 80px;
  padding: 35px 45px 25px;
  display: flex;
  flex-direction: column;
  align-content: center;
  text-align: center;
`;

const ResetPasswordHeader = styled.div`
  display: inline-block;
  margin: 20px;
  margin-bottom: 10px;
`;

const ErrorMessage = styled(Text)`
  font-size: 12px;
  line-height: 16px;
  color: #ea6e6d;
  padding-bottom: 1em;
  white-space: pre;
`;

const ResetPassword = () => {
  const [tokenVerificationError, setTokenVerificationError] = useState(null);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const emailParam = queryParams.get('email');
  const tokenParam = queryParams.get('token');

  useEffect(() => {
    if (!emailParam || !tokenParam) {
      navigateToLogin();
      return;
    }

    const loginToken = async () => {
      //Check if the URL token and email match what is in the database so
      //that a user can access the page
      //Expect a 200 response for matching, or a 4xx for unmatched
      try {
        await verifyTokenMatches(emailParam, tokenParam);
      } catch (error) {
        navigateToLogin();
        return;
      }

      setEmail(emailParam);
      setToken(tokenParam);
    };
    loginToken();
  }, [emailParam, tokenParam]);

  /**
   * Navigate to login when processes finish. Sometimes reach-router does not
   * have the ability to navigate immediately and gets stopped.
   */
  const navigateToLogin = () => {
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };
  /**
   *  @function submitPasswordReset
   *
   *   When the function is called it will get email, token, and password for the resetPassword function invocation
   *   and will set showSuccessModal to true. If an error occurs tokenVerificationError will be set to UNEXPECTED_ERROR_MESSAGE
   *
   *  @param password {string} - The new password that the user wants to use after submit
   *
   *  @return {promise} - Will return a promise. If that promise resolves that would mean resetPassword function complete successfully
   */

  const submitPasswordReset = async password => {
    try {
      await resetPassword(email, token, password);
      setShowSuccessModal(true);
    } catch (err) {
      const UNEXPECTED_ERROR_MESSAGE =
        'An error occurred while attempting to verify your account.\nPlease try again, or contact a system administrator if the problem persists.';
      setTokenVerificationError(UNEXPECTED_ERROR_MESSAGE);
    }
  };

  return (
    <ResetPasswordPage>
      <ResetPasswordPanel>
        <Logo
          width="110px"
          height="50px"
          display="inline-block"
          margin="0.5em auto"
        />
        <ResetPasswordHeader>
          <Text fontSize="22px">Reset Password</Text>
        </ResetPasswordHeader>

        <SetNewPasswordForm
          onSubmit={submitPasswordReset}
          isError={tokenVerificationError ? true : false}
        />

        {tokenVerificationError ? (
          <React.Fragment>
            <ErrorMessage>{tokenVerificationError}</ErrorMessage>
          </React.Fragment>
        ) : null}
      </ResetPasswordPanel>

      {showSuccessModal ? (
        <ConfirmationModal
          updateModalState={navigateToLogin}
          header="Password Reset"
          message="Your password has been successfully updated! Please login to continue."
        />
      ) : null}
    </ResetPasswordPage>
  );
};

export default ResetPassword;
